import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { KeyRound, Loader2 } from "lucide-react";
import { useSelector } from "react-redux";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import routes from "routes/routes";
import { RootState } from "../../../redux/store";
import { useAppDispatch } from "../../../redux/useDispatch";
import {
  usePasskeyLoginOptionsMutation,
  usePasskeyLoginVerifyMutation,
  useRequestOtpMutation,
} from "api/account/accountApiSlice";
import {
  setAccountLoginEmail,
  setAccountProfile,
} from "../../../redux/slice/account/accountSlice";
import { setAccountSessionToken } from "authentication/accountSession";
import { startAuthentication } from "@simplewebauthn/browser";
import { isPasskeySupported } from "../common/passkeyPrompt";
import {
  cleanEmail,
  getEmailSuggestion,
  isEmailAcceptable,
} from "util/validation";
import AccountPageLayout from "../components/AccountPageLayout";
import { readReturnTo, withReturnTo } from "../common/returnTo";
import {
  buildInviteReturnTo,
  readInviteEmail,
  readInviteOrganizationId,
} from "../common/invite";

/**
 * H-3a account login — email step. Requests an OTP and moves to the verify
 * step. The outcome is uniform whether or not an account exists for the email
 * (the backend always answers 204 — enumeration safety), so the page never
 * reveals account existence. The email travels to the verify step via Redux
 * flow state, never via the URL.
 */
const AccountLoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [requestOtp, { isLoading: isSendingCode }] = useRequestOtpMutation();
  // H-3a passkeys (#1901): the fast path. OTP below stays the visible
  // fallback — this button is an addition to the screen, never a gate.
  const [passkeyOptions, { isLoading: isStartingPasskey }] =
    usePasskeyLoginOptionsMutation();
  const [passkeyVerify, { isLoading: isVerifyingPasskey }] =
    usePasskeyLoginVerifyMutation();

  const profile = useSelector((state: RootState) => state.account.profile);
  // Pre-set flow email (e.g. the soft-signup prompt after a guest booking
  // carries the checkout email over) — used as the initial value only.
  const presetEmail = useSelector(
    (state: RootState) => state.account.loginEmail
  );

  // Invite deep link (#1902): the mail prefills the invited address and names
  // the organization, so the landing after OTP can confirm that membership.
  const inviteOrganizationId = readInviteOrganizationId(location.search);
  const inviteEmail = readInviteEmail(location.search);

  const [email, setEmail] = useState(inviteEmail ?? presetEmail ?? "");
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<string | null>(null);

  // An explicit returnTo wins; otherwise an invite sets its own landing.
  const returnTo =
    readReturnTo(location.search) ??
    (inviteOrganizationId ? buildInviteReturnTo(inviteOrganizationId) : null);

  // Already logged in — nothing to do here; go where the user was headed
  // (never back into the login flow itself, which would loop).
  if (profile) {
    const target =
      returnTo && !returnTo.startsWith(routes.accountLoginPath)
        ? returnTo
        : routes.accountPath;
    return <Navigate to={target} replace />;
  }

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    // Clear errors while typing; full validation runs on submit.
    setEmailError(null);
    setSuggestion(null);
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    setEmail(suggestion);
    setEmailError(null);
    setSuggestion(null);
  };

  const handlePasskeyLogin = async () => {
    setPasskeyError(null);
    try {
      const { challengeId, options } = await passkeyOptions().unwrap();
      // Discoverable credential: the authenticator picks the account, so the
      // user never types an address on this path.
      const response = await startAuthentication({
        optionsJSON: options as never,
      });
      const result = await passkeyVerify({
        challengeId,
        response: response as unknown as Record<string, unknown>,
      }).unwrap();
      setAccountSessionToken(result.token);
      dispatch(setAccountProfile(result.profile));
      navigate(returnTo ?? routes.accountPath, { replace: true });
    } catch {
      // Includes the user simply cancelling the OS dialog, so the copy points
      // back at the code path rather than reading like a failure.
      setPasskeyError(t("account.passkey.login_failed"));
    }
  };

  const handleSubmit = async () => {
    const cleanedEmail = cleanEmail(email);
    if (!cleanedEmail) {
      setEmailError(t("account.login.error_required"));
      return;
    }

    const suggested = getEmailSuggestion(cleanedEmail);
    if (suggested) {
      setSuggestion(suggested);
      setEmailError(t("account.login.error_typo_suggestion"));
      return;
    }

    if (!isEmailAcceptable(cleanedEmail)) {
      setEmailError(t("account.login.error_invalid"));
      return;
    }

    try {
      await requestOtp({ email: cleanedEmail }).unwrap();
      dispatch(setAccountLoginEmail(cleanedEmail));
      navigate(withReturnTo(routes.accountLoginVerifyPath, returnTo));
    } catch {
      // Technical failure only (network / 5xx) — existence is never leaked.
      setEmailError(t("account.login.error_send_failed"));
    }
  };

  return (
    <AccountPageLayout
      title={
        inviteOrganizationId
          ? t("account.invite.login_title")
          : t("account.login.title")
      }
    >
      <p className="text-center text-sm text-slate-600">
        {inviteOrganizationId
          ? t("account.invite.login_description")
          : t("account.login.description")}
      </p>

      <div className="space-y-2">
        <label
          htmlFor="account-email"
          className="text-sm font-medium text-slate-700"
        >
          {t("account.login.email_label")}
        </label>
        <Input
          id="account-email"
          type="email"
          autoComplete="email"
          placeholder={t("account.login.email_placeholder")}
          value={email}
          onChange={handleEmailChange}
          error={Boolean(emailError)}
          disabled={isSendingCode}
          className="w-full"
        />
        {emailError && <p className="text-sm text-red-600">{emailError}</p>}
        {suggestion && (
          <button
            type="button"
            onClick={applySuggestion}
            className="inline-flex items-center rounded-full border border-primary px-3 py-1 text-sm text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            {suggestion}
          </button>
        )}
      </div>

      {isPasskeySupported() && (
        <div className="space-y-2">
          <Button
            size="lg"
            variant="outline"
            className="w-full"
            disabled={isStartingPasskey || isVerifyingPasskey}
            onClick={handlePasskeyLogin}
          >
            {isStartingPasskey || isVerifyingPasskey ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {t("account.passkey.signing_in")}
              </>
            ) : (
              <>
                <KeyRound className="mr-2 h-5 w-5" />
                {t("account.passkey.login_button")}
              </>
            )}
          </Button>
          {passkeyError && (
            <p className="text-sm text-red-600">{passkeyError}</p>
          )}
          <p className="text-center text-xs text-slate-500">
            {t("account.passkey.or_use_code")}
          </p>
        </div>
      )}

      <Button
        size="lg"
        variant="primary"
        className="w-full"
        disabled={isSendingCode || !email.trim()}
        onClick={handleSubmit}
      >
        {isSendingCode ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            {t("account.login.sending")}
          </>
        ) : (
          t("account.login.submit")
        )}
      </Button>
    </AccountPageLayout>
  );
};

export default AccountLoginPage;
